import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Shield, LogOut, Coins } from "lucide-react";
import { ViewBoxMark } from "@/components/ViewBox";

export default function Navbar({ onOpenAuth }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  return (
    <header
      data-testid="main-navbar"
      className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-slate-200/80"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-6 lg:px-10 h-16">
        <Link to="/" className="flex items-center gap-2.5" data-testid="brand-link">
          <div className="relative flex h-9 w-9 items-center justify-center rounded-lg bg-slate-950">
            <ViewBoxMark size={22} />
          </div>
          <div className="flex items-baseline gap-2">
            <span className="font-display text-lg font-extrabold tracking-tight text-slate-900">ViewBox</span>
            <span className="font-display text-lg font-extrabold tracking-tight text-emerald-700">AI</span>
            <span className="hidden md:inline text-[10px] font-mono uppercase tracking-widest text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">
              Healthcare Builder
            </span>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-7 text-sm font-medium text-slate-600">
          <Link to="/templates" className="hover:text-emerald-900 transition-colors" data-testid="nav-templates">Templates</Link>
          <Link to="/marketplace" className="hover:text-emerald-900 transition-colors" data-testid="nav-marketplace">Marketplace</Link>
          <Link to="/pricing" className="hover:text-emerald-900 transition-colors" data-testid="nav-pricing">Pricing</Link>
          <Link to="/compliance" className="hover:text-emerald-900 transition-colors" data-testid="nav-compliance">Compliance</Link>
          {user && <Link to="/dashboard" className="hover:text-emerald-900 transition-colors" data-testid="nav-dashboard">Dashboard</Link>}
        </nav>

        <div className="flex items-center gap-2">
          <div className="hidden sm:flex items-center gap-1.5 text-xs font-mono text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1">
            <Shield className="h-3 w-3" /> HIPAA-Aware
          </div>
          {!user ? (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onOpenAuth("login")}
                data-testid="btn-nav-signin"
              >Sign in</Button>
              <Button
                size="sm"
                className="bg-emerald-900 hover:bg-emerald-800 text-white"
                onClick={() => onOpenAuth("register")}
                data-testid="btn-nav-getstarted"
              >Start building</Button>
            </>
          ) : (
            <>
              <Link to="/pricing" className="hidden sm:flex items-center gap-1.5 text-xs font-mono text-emerald-900 bg-emerald-100 border border-emerald-300 rounded-full px-2.5 py-1 hover:bg-emerald-200 transition-colors" data-testid="credits-badge">
                <Coins className="h-3 w-3" /> {user.credits ?? 0} credits
              </Link>
              <div className="hidden sm:flex items-center gap-2 text-sm">
                <span className="capitalize text-xs font-semibold text-emerald-900 bg-emerald-100 px-2 py-1 rounded-md" data-testid="user-role-badge">
                  {user.role}
                </span>
                <span className="text-slate-700" data-testid="user-name">{user.full_name}</span>
              </div>
              <Button
                variant="ghost" size="sm"
                onClick={() => { logout(); nav("/"); }}
                data-testid="btn-logout"
              ><LogOut className="h-4 w-4 mr-1" />Logout</Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
